import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    ShieldCheck,
    Copy,
    Check,
    KeyRound,
    ArrowRight,
    Rocket,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const ANIMAL_OPTIONS = ["dogs", "cats", "puppies", "kittens", "exotic", "farm", "senior", "special-needs"];
const NEED_OPTIONS = ["food", "volunteers", "fosters", "funds", "supplies", "medical", "transport"];
const RESOURCE_OPTIONS = ["transport", "foster", "volunteers", "vet", "food", "supplies", "training"];
const FOCUS_OPTIONS = ["meat-trade", "street-dogs", "puppy-mill", "medical", "senior", "cross-border", "sterilization"];

function TagRow({ options, value, onChange, testid }) {
    const toggle = (o) => {
        onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
    };
    return (
        <div className="flex flex-wrap gap-2" data-testid={testid}>
            {options.map((o) => (
                <button
                    type="button"
                    key={o}
                    onClick={() => toggle(o)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                        value.includes(o)
                            ? "bg-[var(--rl-primary)] text-white border-[var(--rl-primary)]"
                            : "bg-white text-[var(--rl-text-2)] border-[var(--rl-border)] hover:bg-[var(--rl-cream)]"
                    }`}
                    data-testid={`${testid}-${o}`}
                >
                    {o}
                </button>
            ))}
        </div>
    );
}

function Field({ label, children, required, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="eyebrow">{label}{required && <span className="text-[var(--rl-urgent)] ml-1">*</span>}</span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}

export default function JoinRescue() {
    const nav = useNavigate();
    const { login } = useAuth();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const [form, setForm] = useState({
        org_name: "", email: "", password: "", contact_person: "",
        phone: "", website: "", location: "", state: "",
        country: "USA", region: "North America",
        description: "", animal_types: [], focus: [],
        current_needs: [], resources_offered: [],
        donation_url: "", social_instagram: "", social_twitter: "", social_facebook: "",
    });
    const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });
    const setV = (k) => (v) => setForm({ ...form, [k]: v });

    async function submit(e) {
        e.preventDefault();
        if (!form.org_name.trim()) return toast.error("Organization name is required");
        if (!form.email.trim()) return toast.error("Contact email is required");
        if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
        if (!form.contact_person.trim()) return toast.error("Contact person is required");
        if (!form.location.trim()) return toast.error("Location is required");

        setBusy(true);
        try {
            const social_links = {};
            if (form.social_instagram) social_links.instagram = form.social_instagram;
            if (form.social_twitter) social_links.twitter = form.social_twitter;
            if (form.social_facebook) social_links.facebook = form.social_facebook;
            const body = {
                org_name: form.org_name,
                email: form.email,
                password: form.password,
                contact_person: form.contact_person,
                phone: form.phone || null,
                website: form.website || null,
                location: form.location,
                state: form.state || null,
                country: form.country,
                region: form.region,
                description: form.description || null,
                animal_types: form.animal_types,
                focus: form.focus,
                current_needs: form.current_needs,
                resources_offered: form.resources_offered,
                donation_url: form.donation_url || null,
                social_links: Object.keys(social_links).length ? social_links : null,
            };
            const { data } = await api.post("/rescues/register", body);
            // Auto-login the new rescue user
            localStorage.setItem("rl_token", data.token);
            try { await login(form.email, form.password); } catch { /* ignore */ }
            setResult(data);
            toast.success("Rescue registered — welcome to the network.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not register");
        }
        setBusy(false);
    }

    function copyKey() {
        if (!result?.api_key) return;
        navigator.clipboard.writeText(result.api_key);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    if (result) {
        return (
            <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16" data-testid="join-success">
                <div className="w-16 h-16 rounded-full bg-[var(--rl-success)] text-white flex items-center justify-center">
                    <Rocket className="w-8 h-8" />
                </div>
                <h1 className="font-serif text-4xl lg:text-5xl mt-6 tracking-tight">
                    {result.rescue.name} is on the network.
                </h1>
                <p className="text-[var(--rl-text-2)] mt-3 max-w-xl">
                    Registration is pending verification (usually &lt; 24 hours). Meanwhile, your rescue portal, dashboard, and API key are already live.
                </p>

                <div className="mt-8 card-warm rounded-2xl p-6 lg:p-8 border-2 border-[var(--rl-primary)]" data-testid="api-key-panel">
                    <div className="eyebrow text-[var(--rl-primary)] mb-2 inline-flex items-center gap-2">
                        <KeyRound className="w-3.5 h-3.5" /> Your API key · shown once
                    </div>
                    <div className="font-mono text-sm bg-[var(--rl-cream)] rounded-lg p-4 break-all border border-[var(--rl-border)]" data-testid="api-key-value">
                        {result.api_key}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                        <Button onClick={copyKey} className="rounded-full h-10 px-4" data-testid="copy-key-btn">
                            {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy key</>}
                        </Button>
                        <span className="text-xs text-[var(--rl-text-3)]">{result.notice}</span>
                    </div>
                    <div className="mt-4 text-xs text-[var(--rl-text-2)]">
                        Use as <code className="font-mono bg-[var(--rl-cream)] px-1.5 py-0.5 rounded">X-API-Key</code> header on any <code className="font-mono bg-[var(--rl-cream)] px-1.5 py-0.5 rounded">/api/rescues/me/*</code> endpoint. See <Link className="text-[var(--rl-primary)] font-semibold" to="/developers">API docs →</Link>
                    </div>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    <Link to="/rescue-portal" data-testid="go-portal">
                        <Button className="btn-primary rounded-full h-12 px-5 w-full">Open rescue portal <ArrowRight className="w-4 h-4 ml-2" /></Button>
                    </Link>
                    <Link to="/developers">
                        <Button variant="outline" className="rounded-full h-12 px-5 w-full border-[var(--rl-border-strong)]">Read the API docs</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="join-page">
            {/* Hero */}
            <section className="bg-gradient-to-br from-[var(--rl-cream)] to-[#eef2ea] border-b border-[var(--rl-border)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7">
                        <div className="eyebrow mb-4 inline-flex items-center gap-2 text-[var(--rl-primary)]">
                            <ShieldCheck className="w-3.5 h-3.5" /> Register your rescue
                        </div>
                        <h1 className="font-serif text-[42px] leading-[1.05] sm:text-6xl tracking-tight">
                            Get listed.<br />
                            <em className="not-italic text-[var(--rl-primary)]">Plug into every resource</em>{" "}
                            in 2 minutes.
                        </h1>
                        <p className="mt-6 text-lg text-[var(--rl-text-2)] max-w-xl leading-relaxed">
                            List your rescue, post current needs, publish what you can offer other rescues, and get an API key for automated integrations with your intake / adoption software.
                        </p>
                    </div>
                    <div className="lg:col-span-5 grid grid-cols-1 gap-3">
                        <Perk icon={Sparkles} title="Rescue portal + dashboard" body="Manage your listing, post needs, list resources you offer." />
                        <Perk icon={KeyRound} title="Personal API key" body="Sync dogs, needs, and resources programmatically." />
                        <Perk icon={ShieldCheck} title="Alert network reach" body="Broadcast urgent asks to donors, fosters, drivers." />
                    </div>
                </div>
            </section>

            {/* Form */}
            <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">
                <form onSubmit={submit} className="space-y-8" data-testid="join-form">
                    {/* Section 1: Org */}
                    <Section title="Your organization" step={1}>
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Organization name" required>
                                <Input value={form.org_name} onChange={set("org_name")} data-testid="join-org-name" />
                            </Field>
                            <Field label="Website">
                                <Input value={form.website} onChange={set("website")} placeholder="https://" data-testid="join-website" />
                            </Field>
                            <Field label="Short description" className="md:col-span-2">
                                <Textarea value={form.description} onChange={set("description")} placeholder="2–3 sentences on your mission and reach." data-testid="join-description" />
                            </Field>
                        </div>
                    </Section>

                    {/* Section 2: Location + contact */}
                    <Section title="Location &amp; contact" step={2}>
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Location (city, state/region)" required>
                                <Input value={form.location} onChange={set("location")} data-testid="join-location" />
                            </Field>
                            <Field label="Country" required>
                                <Input value={form.country} onChange={set("country")} data-testid="join-country" />
                            </Field>
                            <Field label="Region">
                                <Select value={form.region} onValueChange={setV("region")}>
                                    <SelectTrigger data-testid="join-region"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {["North America", "South America", "Europe", "Asia", "Africa", "Oceania"].map((r) => (
                                            <SelectItem key={r} value={r}>{r}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="State / Province">
                                <Input value={form.state} onChange={set("state")} data-testid="join-state" />
                            </Field>
                            <Field label="Contact person" required>
                                <Input value={form.contact_person} onChange={set("contact_person")} data-testid="join-contact-person" />
                            </Field>
                            <Field label="Contact phone">
                                <Input value={form.phone} onChange={set("phone")} data-testid="join-phone" />
                            </Field>
                        </div>
                    </Section>

                    {/* Section 3: Animals + focus */}
                    <Section title="What you rescue" step={3}>
                        <Field label="Animal types">
                            <TagRow options={ANIMAL_OPTIONS} value={form.animal_types} onChange={setV("animal_types")} testid="join-animals" />
                        </Field>
                        <Field label="Focus areas" className="mt-4">
                            <TagRow options={FOCUS_OPTIONS} value={form.focus} onChange={setV("focus")} testid="join-focus" />
                        </Field>
                    </Section>

                    {/* Section 4: Needs + resources */}
                    <Section title="Current needs &amp; resources you can offer" step={4}>
                        <Field label="Current needs (what donors + partners should know you need)">
                            <TagRow options={NEED_OPTIONS} value={form.current_needs} onChange={setV("current_needs")} testid="join-needs" />
                        </Field>
                        <Field label="Resources you can offer other rescues" className="mt-4">
                            <TagRow options={RESOURCE_OPTIONS} value={form.resources_offered} onChange={setV("resources_offered")} testid="join-resources" />
                        </Field>
                    </Section>

                    {/* Section 5: Donation + social */}
                    <Section title="Donation page &amp; social" step={5}>
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Donation page URL">
                                <Input value={form.donation_url} onChange={set("donation_url")} placeholder="https://" data-testid="join-donation-url" />
                            </Field>
                            <Field label="Instagram handle">
                                <Input value={form.social_instagram} onChange={set("social_instagram")} placeholder="@handle" data-testid="join-instagram" />
                            </Field>
                            <Field label="Twitter / X handle">
                                <Input value={form.social_twitter} onChange={set("social_twitter")} placeholder="@handle" data-testid="join-twitter" />
                            </Field>
                            <Field label="Facebook page">
                                <Input value={form.social_facebook} onChange={set("social_facebook")} placeholder="URL or handle" data-testid="join-facebook" />
                            </Field>
                        </div>
                    </Section>

                    {/* Section 6: Account */}
                    <Section title="Your account" step={6}>
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Email (login + notifications)" required>
                                <Input type="email" value={form.email} onChange={set("email")} data-testid="join-email" />
                            </Field>
                            <Field label="Password (min 8 chars)" required>
                                <Input type="password" value={form.password} onChange={set("password")} data-testid="join-password" />
                            </Field>
                        </div>
                    </Section>

                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-xs text-[var(--rl-text-3)] max-w-md">
                            By registering you agree that RescueLink can verify your organization and list it publicly. You can request removal at any time.
                        </div>
                        <Button type="submit" disabled={busy} className="btn-primary rounded-full h-12 px-6" data-testid="join-submit">
                            {busy ? "Registering…" : <>Register &amp; get API key <ArrowRight className="w-4 h-4 ml-2" /></>}
                        </Button>
                    </div>
                </form>
            </section>
        </div>
    );
}

function Section({ title, step, children }) {
    return (
        <div className="card-warm rounded-2xl p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-5">
                <span className="w-7 h-7 rounded-full bg-[var(--rl-primary)] text-white text-xs font-semibold flex items-center justify-center">{step}</span>
                <h3 className="font-serif text-2xl">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function Perk({ icon: Icon, title, body }) {
    return (
        <div className="card-warm rounded-xl p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--rl-cream)] flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-[var(--rl-primary)]" />
            </div>
            <div>
                <div className="font-semibold text-sm">{title}</div>
                <div className="text-xs text-[var(--rl-text-2)] mt-0.5">{body}</div>
            </div>
        </div>
    );
}
